import { IApiErrorResponse, IApiStandardResponse, IRequestPaginationData } from '@/core/models/api/api.models';
import { Console } from '@/shared/providers/custom-logger';
import { Injectable, inject } from '@angular/core';
import { IApiSaldoPunti } from '@models/balance.models';
import { IPremi } from '@models/premio.model';
import { IApiElencoPuntiPremio, ISponsorValidate } from '@models/segnalatore.model';
import { ApiService } from '@services/api/api.service';
import { AppStore } from '@services/store/app.store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Servizio per la gestione delle operazioni relative ai segnalatori.
 * Questo servizio fornisce metodi per:
 * - verificare l'iscrizione dei segnalatori alla manifestazione attiva
 * - controllare l'assegnabilità degli utenti agli sponsor
 * - gestire il saldo punti e i movimenti
 * - recuperare l'elenco dei premi disponibili
 * - gestire tutte le operazioni relative ai segnalatori
 */
@Injectable({
  providedIn: 'root',
})
export class SegnalatoreService {
  private api = environment.API_ENDPOINT;
  private apiService = inject(ApiService);
  private appStore = inject(AppStore);

  /**
   * Controlla che lo sponsor indicato sia iscritto alla manifestazione attiva e che quindi sia possibile assegnare l'utente allo sponsor.
   * @param id_segn ID del segnalatore da verificare
   * @returns Observable che restituisce lo stato di validazione dello sponsor
   */
  checkIscrizioneManifestazioneAttiva(id_segn: number): Observable<ISponsorValidate> {
    return this.apiService
      .get<{ message?: string; success?: boolean }>(
        `/segnalatore/${id_segn}/iscritto-manifestazione-attiva`
      )
      .pipe(map((response) => this.normalizzaRispostaVerificaSponsor(response)));
  }

  /**
   * Controlla che l'utente possa essere assegnato allo sponsor specificato.
   * @param id_segn ID del segnalatore
   * @param id_sponsor ID dello sponsor
   * @returns Observable che restituisce lo stato di validazione dell'assegnazione
   */
  checkSponsorAssegnabile(id_segn: number, id_sponsor: number): Observable<ISponsorValidate> {
    return this.apiService
      .get<{ message?: string; success?: boolean }>(
        `/segnalatore/${id_segn}/assegnabile/${id_sponsor}`
      )
      .pipe(map((response) => this.normalizzaRispostaVerificaSponsor(response)));
  }

  /**
   * Le API di verifica sponsor rispondono con `{ message }` (HTTP 200 = ok).
   * Il campo `success` è assente nel payload attuale: senza normalizzazione il client lo interpreta come fallimento.
   */
  private normalizzaRispostaVerificaSponsor(response: {
    message?: string;
    success?: boolean;
  }): ISponsorValidate {
    return {
      success: response.success !== false,
      message: response.message ?? '',
    };
  }

  /**
   * Verifica se il segnalatore è attivo sulla piattaforma web.
   * @param id_segn ID del segnalatore da verificare
   * @returns Observable che restituisce lo stato di attivazione web
   */
  checkAttivoWeb(id_segn: number): Observable<string> {
    return this.apiService.get<{ message: string }>(
      `/user/registrazione/valida-sponsor/${id_segn}`
    ).pipe(
      map(response => response.message),
      catchError((error: IApiErrorResponse) => {
        Console.log('Errore nella validazione del sponsor:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera il saldo punti corrente del segnalatore.
   * @returns Observable che restituisce il saldo punti come numero
   */
  getSaldoPunti(): Observable<number> {
    return this.apiService.get<IApiSaldoPunti>(
      '/manifestazione/saldo-punti'
    ).pipe(
      map((response: IApiSaldoPunti) => {
        return Number(response.data.saldo_punti);
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log('Errore nel recupero del saldo punti:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera l'elenco delle operazioni e i relativi punti guadagnati con supporto per la paginazione.
   * @param pagination Parametri di paginazione per filtrare i risultati
   * @returns Observable che restituisce l'elenco dei movimenti punti
   */
  getMovimentiPunti(pagination: IRequestPaginationData): Observable<IApiElencoPuntiPremio> {
    const queryParams = new URLSearchParams();
        
    if (pagination.per_page) {
      queryParams.append('per_page', pagination.per_page.toString());
    }
    if (pagination.page) {
      queryParams.append('page', pagination.page.toString());
    }
    if (pagination.sort_by) {
      queryParams.append('sort_by', pagination.sort_by);
    }
    if (pagination.sort_order) {
      queryParams.append('sort_order', pagination.sort_order);
    }

    return this.apiService.get<IApiElencoPuntiPremio>(
      `/manifestazione/punti-premio?${queryParams.toString()}`
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        Console.log('Errore nel recupero dei movimenti punti:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera l'elenco dei premi disponibili.
   * Prima controlla se i premi sono già presenti nello store, altrimenti li recupera dall'API.
   * @returns Observable che restituisce la lista dei premi disponibili
   */
  getPremi(): Observable<IPremi> {
    // Prima controlliamo se abbiamo già i premi nello store
    const premiInStore = this.appStore.premi();
    if (premiInStore.length > 0) {
      // Creiamo un oggetto IPremi con i dati dallo store
      const premiResponse: IPremi = {
        current_page: 1,
        data: premiInStore,
        first_page_url: '',
        from: 1,
        last_page: 1,
        last_page_url: '',
        links: [],
        next_page_url: '',
        path: '',
        per_page: premiInStore.length,
        prev_page_url: '',
        to: premiInStore.length,
        total: premiInStore.length
      };
      return of(premiResponse);
    }

    // Se non ci sono premi nello store, li recuperiamo dall'API
    return this.apiService.get<IPremi>('/manifestazione/premi').pipe(
      tap((response_payload: IPremi) => {
        // Salviamo i premi nello store
        this.appStore.setPremi(response_payload.data);
      }),
      catchError((error: IApiErrorResponse) => {
        Console.log('Errore nel recupero dei premi:', error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera l'elenco completo dei segnalatori.
   * @returns Observable che restituisce la lista dei segnalatori
   */
  getSegnalatori(): Observable<any> {
    return this.apiService.get<any>('/segnalatori');
  }

  /**
   * Invia una richiesta per ottenere un premio specifico.
   * @param id_premio L'identificativo univoco del premio da richiedere
   * @param nota Nota associata alla richiesta (opzionale)
   * @returns Observable che restituisce la risposta standard dell'API
   */
  richiediPremio(id_premio: number, nota: string): Observable<IApiStandardResponse<any>> {
    const payload = nota ? { id_premio, nota } : { id_premio };
    
    return this.apiService.post<IApiStandardResponse<any>, { id_premio: number, nota?: string }>(
      'manifestazione/richiesta-premio',
      payload
    ).pipe(
      map(response => response),
      catchError(error => {
        Console.log('Errore durante la richiesta del premio:', error, 'err');
        return throwError(() => error);
      })
    );
  }
}
